import React, { useContext, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import { VehicleContext } from '../SingleVehicle'
import {
  FUEL_CARD_STATE,
  fuelCardState,
  groupCardNumber,
  maskCardNumber,
} from './fuelCardStatus'

const PROVIDERS = [
  'Allstar',
  'Keyfuels',
  'UK Fuels',
  'Shell',
  'BP',
  'Esso',
  'Texaco',
  'Radius',
]

const MONTHS = moment.monthsShort().map((label, index) => ({
  value: String(index + 1).padStart(2, '0'),
  label: `${String(index + 1).padStart(2, '0')} - ${label}`,
}))

const yearOptions = (existingYear) => {
  const current = moment().year()
  const years = Array.from({ length: 12 }, (_, i) => current - 1 + i)
  if (existingYear && !years.includes(existingYear)) years.unshift(existingYear)
  return years
}

const CardDialog = ({ card, onClose, onSaved }) => {
  const { vehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const isEdit = !!card?._id
  const existingExpiry = card?.expiry ? moment(card.expiry) : null
  const [provider, setProvider] = useState(card?.provider ?? '')
  const [cardNumber, setCardNumber] = useState(
    card?.cardNumber ? groupCardNumber(card.cardNumber) : '',
  )
  const [month, setMonth] = useState(existingExpiry ? existingExpiry.format('MM') : '')
  const [year, setYear] = useState(existingExpiry ? existingExpiry.year() : '')
  const [notes, setNotes] = useState(card?.notes ?? '')
  const [cancelled, setCancelled] = useState(!!card?.cancelled)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState(null)

  const digits = cardNumber.replace(/[\s-]/g, '')
  const canSave = !saving && /^[0-9A-Za-z]{4,30}$/.test(digits) && month && year

  const save = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!canSave) return
    setSaving(true)
    setError(null)
    const body = {
      provider,
      cardNumber: digits,
      expiryMonth: `${year}-${month}`,
      notes,
      cancelled,
    }
    const res = isEdit
      ? await API.put(`/fleet/${vehicle._id}/fuel-cards/${card._id}`, body)
      : await API.post(`/fleet/${vehicle._id}/fuel-cards`, body)
    if (res.status === 200) {
      setToast({ message: isEdit ? 'Fuel card updated' : 'Fuel card added' })
      onSaved(res.data?.fuelCards ?? [])
      onClose()
    } else {
      setError(res.data?.error || 'Unable to save this fuel card.')
      setSaving(false)
    }
  }

  const remove = async () => {
    setSaving(true)
    const res = await API.delete(`/fleet/${vehicle._id}/fuel-cards/${card._id}`)
    if (res.status === 200) {
      setToast({ message: 'Fuel card removed' })
      onSaved(res.data?.fuelCards ?? [])
      onClose()
    } else {
      setError(res.data?.error || 'Unable to remove this fuel card.')
      setSaving(false)
      setConfirmDelete(false)
    }
  }

  return (
    <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="xs">
      <Box component="form" onSubmit={save}>
        <DialogTitle>{isEdit ? 'Edit fuel card' : 'Add fuel card'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Autocomplete
              freeSolo
              autoSelect
              options={PROVIDERS}
              value={provider}
              onChange={(_e, value) => setProvider(value ?? '')}
              onInputChange={(_e, value) => setProvider(value ?? '')}
              renderInput={(params) => <TextField {...params} label="Provider" size="small" />}
            />
            <TextField
              value={cardNumber}
              onChange={(e) =>
                setCardNumber(groupCardNumber(e.target.value.replace(/[^0-9A-Za-z]/g, '')))
              }
              label="Card number"
              size="small"
              required
              autoComplete="off"
              inputProps={{ inputMode: 'numeric', maxLength: 37 }}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                label="Expiry month"
                size="small"
                required
                fullWidth
              >
                {MONTHS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                label="Expiry year"
                size="small"
                required
                fullWidth
              >
                {yearOptions(existingExpiry?.year()).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              label="Notes"
              placeholder="e.g. Diesel only, held in cab, mileage required at pump"
              size="small"
              multiline
              minRows={2}
              helperText="Never record the card PIN here."
            />
            {isEdit && (
              <FormControlLabel
                control={
                  <Switch checked={cancelled} onChange={(e) => setCancelled(e.target.checked)} />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Cancelled / no longer in use
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Lost, stopped or replaced - kept for reference, no expiry warnings
                    </Typography>
                  </Box>
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {confirmDelete ? (
            <>
              <Typography variant="body2" color="error.main" sx={{ mr: 'auto', pl: 1 }}>
                Remove this card from the vehicle?
              </Typography>
              <Button color="inherit" onClick={() => setConfirmDelete(false)} disabled={saving}>
                Keep
              </Button>
              <Button color="error" variant="contained" onClick={remove} disabled={saving}>
                Remove
              </Button>
            </>
          ) : (
            <>
              {isEdit && (
                <Button
                  color="error"
                  onClick={() => setConfirmDelete(true)}
                  disabled={saving}
                  sx={{ mr: 'auto' }}
                >
                  Remove
                </Button>
              )}
              <Button color="inherit" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={!canSave}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add card'}
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

const FuelCardTile = ({ card, canEdit, onEdit }) => {
  const [revealed, setRevealed] = useState(false)
  const state = fuelCardState(card)
  const meta = FUEL_CARD_STATE[state]
  return (
    <Card
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2.5, opacity: state === 'cancelled' ? 0.6 : 1 }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: 'anMuted.main',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <CreditCardRoundedIcon fontSize="small" />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {card.provider || 'Fuel card'}
          </Typography>
          <Stack direction="row" spacing={0.25} alignItems="center">
            <Typography
              fontWeight={700}
              sx={{ fontFamily: 'monospace', letterSpacing: '0.04em' }}
              noWrap
            >
              {revealed ? groupCardNumber(card.cardNumber) : maskCardNumber(card.cardNumber)}
            </Typography>
            <Tooltip title={revealed ? 'Hide number' : 'Show full number'}>
              <IconButton size="small" onClick={() => setRevealed((prev) => !prev)}>
                {revealed ? (
                  <VisibilityOffOutlinedIcon fontSize="inherit" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="inherit" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" mt={0.25}>
            <Typography variant="body2">
              Expires {moment(card.expiry).format('MM/YY')}
            </Typography>
            {state !== 'ok' && (
              <Chip
                size="small"
                label={meta.label}
                color={meta.tone}
                sx={{ borderRadius: 1, height: 20, fontWeight: 700 }}
              />
            )}
          </Stack>
          {card.notes && (
            <Typography variant="caption" color="text.secondary" component="div" mt={0.5}>
              {card.notes}
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Tooltip title="Edit card">
            <IconButton size="small" onClick={onEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Card>
  )
}

// Fuel cards kept with the vehicle, at the top of the Fuel tab. Anyone can
// see them (crew need to know which card is in the cab); only the fleet
// teams can change them (server: routers/fleetEquipment/vehicleFuelCards.js).
const FuelCards = ({ canEdit }) => {
  const { vehicle, setVehicle } = useContext(VehicleContext)
  const [editing, setEditing] = useState(null) // {} = new, card = edit

  const cards = [...(vehicle?.fuelCards ?? [])].sort((a, b) => {
    // Live cards first, then soonest expiry
    if (!!a.cancelled !== !!b.cancelled) return a.cancelled ? 1 : -1
    return moment(a.expiry) - moment(b.expiry)
  })
  const flagged = cards.filter((card) =>
    ['expired', 'expiringSoon'].includes(fuelCardState(card)),
  )

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Fuel cards
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cards kept with this vehicle
          </Typography>
        </Box>
        {canEdit && (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setEditing({})}>
            Add card
          </Button>
        )}
      </Stack>

      {flagged.length > 0 && (
        <Alert severity={flagged.some((card) => fuelCardState(card) === 'expired') ? 'error' : 'warning'}>
          {flagged
            .map(
              (card) =>
                `${card.provider ? `${card.provider} ` : ''}card ${maskCardNumber(card.cardNumber)} ${
                  fuelCardState(card) === 'expired' ? 'expired' : 'expires'
                } ${moment(card.expiry).format('MM/YY')}`,
            )
            .join(' · ')}
          {canEdit ? ' - order a replacement and update it here.' : ' - let the fleet team know.'}
        </Alert>
      )}

      {cards.length ? (
        <Box
          display="grid"
          gap={1.25}
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }}
        >
          {cards.map((card) => (
            <FuelCardTile
              key={card._id}
              card={card}
              canEdit={canEdit}
              onEdit={() => setEditing(card)}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.disabled">
          No fuel cards recorded for this vehicle.
        </Typography>
      )}

      {editing && (
        <CardDialog
          card={editing}
          onClose={() => setEditing(null)}
          onSaved={(fuelCards) => setVehicle((prev) => ({ ...prev, fuelCards }))}
        />
      )}
    </Stack>
  )
}

export default FuelCards
